import { StandardProps } from "../componentMap";
import { ProductDescriptionSideDrawerUI } from "./ProductDescriptionSideDrawer/ProductDescriptionSideDrawerUI";
import { ProductSpecificationSideDrawerUI } from "./ProductSpecificationSideDrawer/ProductSpecificationSideDrawerUI";
import { IngredientsSideDrawerUI } from "./IngredientsSideDrawer/IngredientsSideDrawerUI";
import { ProductDeliveryOptionsSideDrawerUI } from "./ProductDeliveryOptionsSideDrawer/ProductDeliveryOptionsSideDrawerUI";
import { ProductBrandSideDrawerUI } from "./ProductBrandSideDrawer/ProductBrandSideDrawerUI";
import { ProductReviewsSideDrawerUI } from "./ProductReviewsSideDrawer/ProductReviewsSideDrawerUI";
import { ProductSizeAndFitSideDrawerUI } from "./ProductSizeAndFitSideDrawer/ProductSizeAndFitSideDrawerUI";
import { BuyingGuidesSideDrawerUI } from "./BuyingGuidesSideDrawer/BuyingGuidesSideDrawerUI";
import { OutletSideDrawerUI } from "./OutletSideDrawer/OutletSideDrawerUI";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { useRouter } from "next/router";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";

import { getDescription } from "@/utils/product/getDescription";
import { sortBy } from "@/utils/sortBy/sortBy";
import { isAvailableToPreOrder } from "@/utils/variant/isAvailableToPreOrder";
import {
  Attribute,
  AvailableOption,
  UnavailableCollectionOption,
} from "@/types/prodcatBaseTypes";
import { getDeliveryPrice } from "../ProductAccordion/components/ProductDeliveryOptionsAccordion/util/getDeliveryPrice";

import { ensureIsActive } from "@/utils/product/ensureIsActive";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly";

import { getSizeAndFitAttributes } from "../ProductAccordion/components/ProductSizeAndFitAccordion/sizeAndFitUtils";
import { isOutlet } from "@/utils/variant/isOutlet";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { isMadeToMeasure } from "@/utils/product/isMadeToMeasure";
import { HowToUseSideDrawerUI } from "./HowToUseSideDrawer/HowToUseSideDrawerUI";
import { GoodToKnow } from "@/components/ProductSideDrawers/GoodToKnow/GoodToKnow";
import { getEan } from "@/utils/variant/getEan";
import { MeasurementsSideDrawerUI } from "./MeasurementsSideDrawer/MeasurementsSideDrawerUI";
import { getMeasurementsAttributes } from "../ProductAccordion/components/ProductMeasurementsAccordion/measurementsUtils";
import {
  DrawerConfiguration,
  productDrawerConfigSchema,
} from "./config/productDrawerConfigSchema";
import styles from "./ProductSideDrawers.module.scss";

const defaultDrawers: DrawerConfiguration = [
  {
    name: "description",
  },
  {
    name: "specification",
  },
  {
    name: "brand",
  },
  {
    name: "deliveryOptions",
  },
];

const transformAvailable = (option: AvailableOption) => ({
  headline: option.headline,
  description: option.description,
  displayPrice: getDeliveryPrice(option),
});

const transformUnavailableCollection = (
  option: UnavailableCollectionOption,
) => ({
  headline: option.headline,
  description: option.description,
  displayPrice: "",
});

const validateConfigFromProps = (
  configuration: unknown,
): DrawerConfiguration | null => {
  const parsedConfig = productDrawerConfigSchema.safeParse(configuration);
  if (parsedConfig.success) {
    return parsedConfig.data;
  }
  return null;
};

export function ProductSideDrawers({
  product,
  runtimeConfig,
  configProps,
  flags,
  experiencesRawObject,
}: Pick<
  StandardProps,
  "product" | "runtimeConfig" | "configProps" | "flags" | "experiencesRawObject"
>) {
  const { variant } = useVariant(product);
  const { asPath } = useRouter();
  const { baseUrl: productUrl } = getBaseUrlAndQuery(asPath);
  const drawers =
    validateConfigFromProps(configProps?.drawerConfig) || defaultDrawers;

  // Show component when RHR layout is active ("rhr") or coexistence layout (empty/default "")
  // Hide when isolated accordion layout is active ("accordion")
  const flagValue = flags.enablePremiumRhrSideDrawers.value;
  if (flagValue === "accordion") {
    return null;
  }

  const showOutletReturnMessage = isOutlet(variant);
  const isRetired = !ensureIsActive(product);
  const isMeteredProduct = isPerMetre(product);
  const isM2M = isMadeToMeasure(product);

  const [endorsement] =
    variant.messaging?.filter((message) => message.type === "endorsement") ||
    [];

  const variantAttributes = variant.attributes || [];
  const primaryAttributes = product.attributes || [];
  const allAttributes = [...primaryAttributes, ...variantAttributes];
  const sortedAttributes = sortBy(allAttributes, (a) => a.displayName || "");

  const available = product.fulfilmentOptions?.available || [];
  const unavailableCollection =
    product.fulfilmentOptions?.unavailableCollection || [];

  const deliveryOptions = [
    ...available.map(transformAvailable),
    ...unavailableCollection.map(transformUnavailableCollection),
  ];

  let productSpecificationAttributes = sortedAttributes;
  let sizeAndFitAttributes: Attribute[] = [];

  if (drawers.some((da) => da.name === "sizeAndFit")) {
    const categoryIds = product?.browsePageHierarchy?.map(
      (bph) => bph.categoryId,
    );
    sizeAndFitAttributes = getSizeAndFitAttributes(
      categoryIds,
      sortedAttributes,
    );

    if (sizeAndFitAttributes?.length > 0) {
      productSpecificationAttributes = sortedAttributes.filter(
        (attr) => !sizeAndFitAttributes.includes(attr),
      );
    }
  }

  const measurementsAttributes = getMeasurementsAttributes(
    allAttributes,
    product.sellingProductType,
  );

  return (
    <div
      data-testid="product:sidedrawers"
      data-component="ProductSideDrawers"
      className={styles.container}
    >
      {drawers.map((drawer) => {
        switch (drawer.name) {
          case "description":
            return (
              <ProductDescriptionSideDrawerUI
                productType={product.__typename}
                displayCode={variant.displayCode}
                description={getDescription(product)}
                icons={product.icons || []}
                links={product.links || []}
                endorsement={endorsement}
                hideDescriptionLinks={
                  drawers.find((a) => a.name === "buyingGuides") !== undefined
                }
                showBeautySocietyLink={configProps?.beautySocietyLink === true}
                key={drawer.name}
              />
            );
          case "specification":
            return (
              <ProductSpecificationSideDrawerUI
                key={drawer.name}
                attributes={productSpecificationAttributes}
                productType={product.__typename}
              />
            );
          case "ingredients":
            return (
              <IngredientsSideDrawerUI
                drawerHeading="Ingredients"
                product={product}
                runtimeConfig={runtimeConfig}
                key={drawer.name}
              />
            );
          case "deliveryOptions":
            return (
              <ProductDeliveryOptionsSideDrawerUI
                key={drawer.name}
                isBladedArticle={
                  variant.sellingRestrictions?.age?.bladedArticle ?? false
                }
                deliveryOptions={deliveryOptions}
                skuId={variant.aliases.skuId}
                isAvailableToOrderOrPreorder={
                  variant.availability?.availableToOrder ||
                  isAvailableToPreOrder(variant)
                }
                stockApiBaseUrl={runtimeConfig.stockApiBaseUrl}
                stockApiKey={runtimeConfig.stockApiKey}
                showExtendedReturnsMessaging={
                  flags.showExtendedReturnsMessaging.value == true
                }
                extendedReturnsDates={{
                  startDate: String(flags.extendedReturnsPeriodStartDate.value),
                  endDate: String(flags.extendedReturnsPeriodEndDate.value),
                  deadlineDate: String(flags.extendedReturnsDeadlineDate.value),
                }}
                isInStoreOnly={isInStoreOnly(variant)}
                isRetired={isRetired}
                isMeteredProduct={isMeteredProduct}
                isM2M={isM2M}
                browsePageHierarchy={product.browsePageHierarchy}
                sellingProductType={product.sellingProductType}
                showOutletReturnMessage={showOutletReturnMessage}
                returnsStartDate="2024-09-26"
                returnsEndDate="2025-01-23"
                showChristmasDeliveryMessaging={
                  flags.showChristmasDeliveryMessaging.value == true
                }
              />
            );
          case "howToUse":
            return (
              <HowToUseSideDrawerUI
                key={drawer.name}
                howtoUseContent={product?.howToUse}
              />
            );
          case "goodToKnow":
            return (
              <GoodToKnow
                key={drawer.name}
                ean={getEan(variant)}
                isChanel={product?.brand?.name.toUpperCase() === "CHANEL"}
                cleanBeautyBaseUrl={runtimeConfig?.cleanBeautyBaseUrl}
              />
            );
          case "measurements":
            return (
              measurementsAttributes.length > 0 &&
              product.sellingProductType && (
                <MeasurementsSideDrawerUI
                  key={drawer.name}
                  attributes={measurementsAttributes}
                />
              )
            );
          case "buyingGuides":
            return (
              <BuyingGuidesSideDrawerUI
                key={drawer.name}
                links={product.links || []}
              />
            );
          case "outlet":
            return <OutletSideDrawerUI key={drawer.name} />;
          case "brand":
            return (
              <ProductBrandSideDrawerUI
                key={drawer.name}
                brand={product.brand}
                sizeGuide={product.sizeGuide}
                disableSizeGuide={configProps?.disableSizeGuide === true}
              />
            );
          case "reviews":
            return (
              <ProductReviewsSideDrawerUI
                key={drawer.name}
                productId={product.aliases.pimProductId}
                reviewsApiKey={runtimeConfig.reviewsApiKey}
                reviewsScriptUrl={runtimeConfig.reviewsScriptUrl}
                reviewSummary={product?.reviewSummary}
                productUrl={productUrl}
                experiencesRawObject={experiencesRawObject}
              />
            );
          case "sizeAndFit":
            return (
              <ProductSizeAndFitSideDrawerUI
                key={drawer.name}
                attributes={sizeAndFitAttributes}
                productType={product.__typename}
              />
            );
          case "specialOffers":
          case "returnsAndExchange":
          default:
            return null;
        }
      })}
    </div>
  );
}
